<template>
  <div class="goods-table">
    <div class="goods-table__loader" v-if="loading">
      <Loader />
    </div>

    <div class="goods-table__table" v-if="!loading">
      <table>
        <thead>
          <tr>
            <td class="name">Название</td>
            <td class="game">Игра</td>
            <td class="features">Лейблы</td>
            <td class="description-id">ID описания</td>
            <td class="updated-at">Дата обновления</td>
            <td class="actions">Действия</td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td class="name">{{ item.display_name }}</td>
            <td class="game">{{ getGameName(item.game) }}</td>
            <td class="features">
              <div class="features__container">
                <span
                  v-for="feature in item.features"
                  :key="feature.key"
                  class="features__label"
                  :title="feature.title"
                >
                  {{ feature.label }}: {{ feature.value }}
                </span>
              </div>
            </td>
            <td class="description-id">
              <a
                :href="`/descriptions/${item.description_id}`"
                class="description-link"
                @click.prevent="goToDescription(item.description_id)"
              >
                {{ item.description_id }}
              </a>
            </td>
            <td class="updated-at">{{ formatDate(item.updated_at) }}</td>
            <td class="actions">
              <button class="goods-table__editBtn" @click="editItem(item)">
                <img src="../../assets/images/pen.svg" alt="edit" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="items.length === 0" class="goods-table__empty">Нет товаров</p>
    </div>

    <div class="goods-table__pagination" v-if="!loading && pagination.total > 0">
      <div class="goods-table__paginationInfo">
        Показано {{ pagination.skip + 1 }}-{{
          Math.min(pagination.skip + pagination.limit, pagination.total)
        }}
        из {{ pagination.total }}
      </div>
      <div class="goods-table__paginationControls">
        <button
          class="goods-table__paginationBtn"
          :disabled="pagination.skip === 0"
          @click="prevPage"
        >
          <img src="../../assets/images/arrowLeft.svg" alt="prev" />
          Назад
        </button>

        <div class="goods-table__paginationPages">
          <button
            v-for="page in getVisiblePages()"
            :key="page"
            class="goods-table__paginationPage"
            :class="{ active: page === getCurrentPage() }"
            @click="goToPage(page - 1)"
          >
            {{ page }}
          </button>
        </div>

        <button
          class="goods-table__paginationBtn"
          :disabled="pagination.skip + pagination.limit >= pagination.total"
          @click="nextPage"
        >
          Вперед
          <img src="../../assets/images/right-arrow.svg" alt="next" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Loader from '../Loader/Loader.vue'
import { GamesNames } from '@/constants'

export default {
  name: 'GoodsTable',
  components: {
    Loader
  },
  props: {
    items: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => ({
        skip: 0,
        limit: 10,
        total: 0
      })
    }
  },
  methods: {
    getGameName(game) {
      return GamesNames[game]
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    goToDescription(descriptionId) {
      this.$router.push(`/descriptions/${descriptionId}`)
    },
    editItem(item) {
      this.$emit('edit', item)
    },
    nextPage() {
      this.$emit('next-page')
    },
    prevPage() {
      this.$emit('prev-page')
    },
    goToPage(page) {
      this.$emit('go-to-page', page)
    },
    getVisiblePages() {
      const totalPages = Math.ceil(this.pagination.total / this.pagination.limit)
      const currentPage = this.getCurrentPage()
      const maxVisible = 5

      if (totalPages <= maxVisible) {
        return Array.from({ length: totalPages }, (_, i) => i + 1)
      }

      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
      let end = Math.min(totalPages, start + maxVisible - 1)

      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1)
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i)
    },
    getCurrentPage() {
      return Math.floor(this.pagination.skip / this.pagination.limit) + 1
    }
  }
}
</script>

<style lang="scss" scoped>
.goods-table {
  display: flex;
  flex-direction: column;

  &__loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 200px;
  }

  &__table {
    background: #131732;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #646464;

    table {
      width: 100%;
      border-collapse: collapse;

      thead {
        background: #1a1f3e;

        tr {
          td {
            padding: 16px 12px;
            color: white;
            font-weight: 600;
            font-size: 14px;
            text-align: left;
            border-bottom: 1px solid #646464;

            &.name {
              width: 200px;
            }

            &.game {
              width: 100px;
            }

            &.features {
              width: 300px;
            }

            &.description-id {
              width: 200px;
            }

            &.updated-at {
              width: 150px;
            }

            &.actions {
              width: 80px;
            }
          }
        }
      }

      tbody {
        tr {
          border-bottom: 1px solid #2a2f4e;

          &:hover {
            background: #1a1f3e;
          }

          &:last-child {
            border-bottom: none;
          }

          td {
            padding: 16px 12px;
            color: white;
            font-size: 14px;
            vertical-align: top;

            &.name {
              font-weight: 500;
            }

            &.game {
              color: #7457ff;
              font-weight: 500;
            }

            &.description-id {
              .description-link {
                color: #7457ff;
                text-decoration: none;
                font-weight: 500;
                transition: all 0.3s ease;

                &:hover {
                  color: #8a6cff;
                  text-decoration: underline;
                }
              }
            }

            &.updated-at {
              color: #ccc;
              font-size: 13px;
            }
          }
        }
      }
    }
  }

  &__empty {
    text-align: center;
    color: #888;
    padding: 40px;
    font-size: 16px;
  }

  &__editBtn {
    border: none;
    outline: none;
    background: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 4px;
    transition: all 0.3s ease;

    img {
      width: 16px;
      height: 16px;
      filter: brightness(0) invert(1);
    }

    &:hover {
      background: #2a2f4e;
    }
  }

  // Пагинация
  &__pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding: 20px;
    background: #131732;
    border-radius: 8px;
    border: 1px solid #646464;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 15px;
    }
  }

  &__paginationInfo {
    color: #ccc;
    font-size: 14px;
    font-weight: 500;
  }

  &__paginationControls {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  &__paginationBtn {
    display: flex;
    align-items: center;
    gap: 8px;
    border: none;
    outline: none;
    background: #1a1f3e;
    color: white;
    padding: 8px 16px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #646464;

    img {
      width: 14px;
      height: 14px;
      filter: brightness(0) invert(1);
    }

    &:hover:not(:disabled) {
      background: #2a2f4e;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &__paginationPages {
    display: flex;
    gap: 5px;
  }

  &__paginationPage {
    border: none;
    outline: none;
    background: #1a1f3e;
    color: white;
    width: 35px;
    height: 35px;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
    font-weight: 500;
    border: 1px solid #646464;

    &:hover {
      background: #2a2f4e;
    }

    &.active {
      background: #7457ff;
      border-color: #7457ff;
    }
  }
}

.features {
  &__container {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  &__label {
    background: #1a1f3e;
    color: #ccc;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid #2a2f4e;
    white-space: nowrap;
  }
}
</style>
